import {useInventory} from "../State/InventoryContext";
import React, {useRef, useState} from "react";
import {GiDigDug} from "react-icons/gi";
import styled from "styled-components";
import {MANUAL_ACTIONS} from "../State/GameData";
import {POLLUTION_IMPACT} from "../State/Pollution";

const M = {
    MiningFieldManual: styled.div`
      width: 80px;
      display: flex;
      flex-direction: column;
      padding-bottom: 16px;
      align-items: center;
    `,
    ManualButton: styled.div<{ $pressed: boolean; $blink: boolean }>`
      background-color: lightgrey;
      border-radius: 8px;
      border: 4px solid white;
      width: 70px;
      height: 70px;
      cursor: pointer;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      ${(props) => ((props.$pressed) ?
            `box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.3);` :
            `box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3), 4px 4px 8px rgba(0, 0, 0, 0.2);`
    )}
      animation: ${(props) => (props.$blink ? "blink-red 0.4s ease-in-out 3" : "none")};

      &:hover {
        background-color: #d3d3d3;
        filter: brightness(1.1);
      }

      @keyframes blink-red {
        0%, 100% {
          border-color: white;
        }
        50% {
          border-color: #ff3333;
          box-shadow: 0 0 12px #ff3333, 2px 2px 4px rgba(0, 0, 0, 0.3);
        }
      }
    `,
    ProgressBarContainer: styled.div`
      position: absolute;
      bottom: 4px;
      left: 4px;
      right: 4px;
      height: 12px;
      background: #2f2f2f;
      border-radius: 4px;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding-right: 2px;
    `,
    ProgressBarFill: styled.div<{ $progress: number }>`
      position: absolute;
      left: 0;
      top: 0;
      width: ${(props) => props.$progress}%;
      height: 100%;
      background: #ff9800;
      transition: width 0.1s linear;
    `,
    ProgressPercent: styled.span`
      font-size: 8px;
      color: #fff;
      font-weight: bold;
      position: relative;
      z-index: 1;
    `,
    FloatingText: styled.div<{ $visible: boolean; $index: number }>`
      position: absolute;
      left: 50%;
      top: 10px;
      transform: translateX(-50%);
      color: #4caf50;
      font-size: 24px;
      font-weight: bold;
      pointer-events: none;
      opacity: ${(props) => (props.$visible ? 1 : 0)};
      animation: ${(props) => (props.$visible ? `float-up 1.2s ease-out` : "none")};
      animation-delay: ${(props) => props.$index * 0.05}s;
      text-shadow: 0 0 4px rgba(0, 0, 0, 0.8), 0 0 8px rgba(76, 175, 80, 0.6);

      @keyframes float-up {
        0% {
          transform: translateX(-50%) translateY(0);
          opacity: 1;
        }
        40% {
          transform: translateX(-50%) translateY(-15px);
          opacity: 1;
        }
        100% {
          transform: translateX(-50%) translateY(-40px);
          opacity: 0;
        }
      }
    `,
}
type FloatingTextInstance = {
    id: number;
    amount: number;
    visible: boolean;
};

export function ManualMiner(props: {type: string, blink?: boolean}) {
    const { updateInventoryItem, addPollution } = useInventory();

    const manualAction = MANUAL_ACTIONS[props.type];
    const cooldownMs = manualAction?.durationMs ?? 1000;
    const reward = manualAction?.reward ?? {itemId: props.type, quantity: 1};
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [floatingTexts, setFloatingTexts] = useState<FloatingTextInstance[]>([]);
    const nextIdRef = useRef(0);

    // Cooldown state
    const [cooldownProgress, setCooldownProgress] = useState(0);
    const [isOnCooldown, setIsOnCooldown] = useState(false);
    const cooldownIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Clean up interval on unmount
    React.useEffect(() => {
        return () => {
            if (cooldownIntervalRef.current) {
                clearInterval(cooldownIntervalRef.current);
            }
        };
    }, []);

    const handleClick = () => {
        // Ignore clicks while on cooldown
        if (isOnCooldown) return;

        // Play mining sound
        if (audioRef.current) {
            audioRef.current.play();
        }

        // Immediately give reward
        updateInventoryItem(reward.itemId, reward.quantity);
        addPollution(POLLUTION_IMPACT.manualAction);

        // Add floating text
        const newId = nextIdRef.current++;
        const newText: FloatingTextInstance = {
            id: newId,
            amount: reward.quantity,
            visible: true,
        };

        setFloatingTexts(prev => [...prev, newText]);

        // Remove after animation completes
        setTimeout(() => {
            setFloatingTexts(prev => prev.filter(text => text.id !== newId));
        }, 1200);

        // Start cooldown
        setIsOnCooldown(true);
        setCooldownProgress(100);

        const stepMs = cooldownMs / 20;
        cooldownIntervalRef.current = setInterval(() => {
            setCooldownProgress(prev => {
                const nextProgress = prev - 5;
                if (nextProgress <= 0) {
                    if (cooldownIntervalRef.current) {
                        clearInterval(cooldownIntervalRef.current);
                    }
                    setIsOnCooldown(false);
                    return 0;
                }
                return nextProgress;
            });
        }, stepMs);
    };

    return <M.MiningFieldManual>
        <audio ref={audioRef}>
            <source src="../Assets/Sounds/pickaxe1.wav" type="audio/wav"/>
        </audio>
        <M.ManualButton
            $pressed={false}
            $blink={props.blink ?? false}
            onClick={handleClick}
            style={{
                cursor: isOnCooldown ? 'not-allowed' : 'pointer',
                opacity: isOnCooldown ? 0.6 : 1,
            }}
        >
            <GiDigDug size={50} />
            {isOnCooldown && (
                <M.ProgressBarContainer>
                    <M.ProgressBarFill $progress={cooldownProgress} />
                    <M.ProgressPercent>{Math.floor(cooldownProgress)}%</M.ProgressPercent>
                </M.ProgressBarContainer>
            )}
            {floatingTexts.map((text, index) => (
                <M.FloatingText
                    key={text.id}
                    $visible={text.visible}
                    $index={index}
                >
                    +{text.amount}
                </M.FloatingText>
            ))}
        </M.ManualButton>
    </M.MiningFieldManual>
}